import React, { useState } from 'react'
import {
  Paper,
  Box,
  TextField,
  Button,
  Divider,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  useTheme as useMuiTheme,
} from '@mui/material'
import TextEditorJob from './TextEditorJob'
import TextEditorAcademic from './TextEditorAcademic'
import TextEditorSkills from './TextEditorSkills'
import TextEditorContacts from './TextEditorContacts'
import { useProfiles } from '../../context/ProfileContext'
import { useTheme } from '../../context/ThemeContext'

const sections = [
  { id: 'experience', label: 'Work Experience' },
  { id: 'education', label: 'Academic Achievements' },
  { id: 'skills', label: 'Skills' },
  { id: 'contacts', label: 'Contacts' },
]

const defaultFormData = {
  experience: {
    entries: [
      {
        id: Date.now(),
        content: '',
        role: '',
        company: '',
        fromDate: '',
        untilDate: '',
      },
    ],
  },
  education: {
    entries: [
      {
        id: Date.now(),
        content: '',
        course: '',
        university: '',
        fromDate: '',
        untilDate: '',
      },
    ],
  },
  skills: {
    entries: [
      {
        id: Date.now(),
        content: '',
      },
    ],
  },
  contacts: {
    name: '',
    email: [{ id: Date.now(), value: '' }],
    phone: [{ id: Date.now(), value: '' }],
    location: '',
    socials: [{ id: Date.now(), value: '' }],
    website: [{ id: Date.now(), value: '' }],
  },
}

const InformationEditor = ({ profile, onSave }) => {
  const { saveProfile } = useProfiles()
  const { isDarkMode } = useTheme()
  const theme = useMuiTheme()
  const [activeSection, setActiveSection] = useState('experience')
  const [formData, setFormData] = useState(profile?.data || defaultFormData)
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [profileName, setProfileName] = useState('')
  const [saving, setSaving] = useState(false)
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' })

  // Use effect to update form data when profile changes
  React.useEffect(() => {
    if (profile) {
      setFormData(profile.data || defaultFormData)
    } else {
      setFormData(defaultFormData)
    }
  }, [profile])

  const handleSectionChange = (event, newValue) => {
    setActiveSection(newValue)
  }

  const handleEntriesChange = (section, newEntries) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        entries: newEntries,
      },
    }))
  }

  const handleContactsChange = (newContacts) => {
    setFormData((prev) => ({
      ...prev,
      contacts: newContacts,
    }))
  }

  const handleSaveClick = () => {
    setSaveDialogOpen(true)
  }

  const handleSaveConfirm = async () => {
    if (!profileName.trim()) {
      setNotification({
        open: true,
        message: 'Please enter a profile name',
        severity: 'error',
      })
      return
    }

    setSaving(true)
    try {
      const result = await saveProfile(formData, profileName)

      if (result.success) {
        setNotification({
          open: true,
          message: 'Profile saved successfully',
          severity: 'success',
        })

        // Call the onSave callback as well to update UI
        if (onSave) {
          onSave(formData, profileName)
        }
      } else {
        setNotification({
          open: true,
          message: `Error saving profile: ${result.error}`,
          severity: 'error',
        })
      }
    } catch (error) {
      setNotification({
        open: true,
        message: `Error: ${error.message}`,
        severity: 'error',
      })
    } finally {
      setSaving(false)
      setSaveDialogOpen(false)
      setProfileName('')
    }
  }

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false })
  }

  const renderSection = (section) => {
    switch (section) {
      case 'experience':
        return (
          <TextEditorJob
            entries={formData[section].entries}
            onEntriesChange={(newEntries) => handleEntriesChange(section, newEntries)}
            sectionType={sections.find((s) => s.id === section).label}
          />
        )
      case 'education':
        return (
          <TextEditorAcademic
            entries={formData[section].entries}
            onEntriesChange={(newEntries) => handleEntriesChange(section, newEntries)}
            sectionType={sections.find((s) => s.id === section).label}
          />
        )
      case 'skills':
        return (
          <TextEditorSkills
            entries={formData[section].entries}
            onEntriesChange={(newEntries) => handleEntriesChange(section, newEntries)}
            sectionType={sections.find((s) => s.id === section).label}
          />
        )
      case 'contacts':
        return (
          <TextEditorContacts
            contacts={formData.contacts}
            onContactsChange={handleContactsChange}
            sectionType={sections.find((s) => s.id === section).label}
          />
        )
      default:
        return null
    }
  }

  return (
    <>
      <Paper
        sx={{
          p: 3,
          width: "clamp('200px', '40%', '400px')",
          margin: 1,
        }}
        elevation={2}
      >
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            mb: 3,
            color: theme.palette.mode === 'dark' ? 'secondary.dark' : 'secondary.light',
          }}
        >
          <Tabs
            value={activeSection}
            onChange={handleSectionChange}
            variant='scrollable'
            scrollButtons='auto'
            allowScrollButtonsMobile
            sx={{
              '& .MuiTabs-scrollButtons': {
                '&.Mui-disabled': {
                  opacity: 0,
                },
              },
            }}
          >
            {sections.map((section) => (
              <Tab
                key={section.id}
                value={section.id}
                label={section.label}
                sx={{
                  color: theme.palette.mode === 'dark' ? 'secondary.dark' : 'secondary.light',
                }}
              />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ mb: 3 }}>{renderSection(activeSection)}</Box>
        <Divider sx={{ mb: 3 }} />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant='contained'
            onClick={handleSaveClick}
            size='large'
            sx={{
              backgroundColor: theme.palette.mode === 'dark' ? 'secondary.dark' : 'secondary.light',
            }}
          >
            Save Profile
          </Button>
        </Box>

        <Dialog open={saveDialogOpen} onClose={() => !saving && setSaveDialogOpen(false)}>
          <DialogTitle>Save Profile</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin='dense'
              label='Profile Name'
              fullWidth
              value={profileName}
              onChange={(e) => setProfileName(e.target.value)}
              disabled={saving}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSaveDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveConfirm}
              variant='contained'
              disabled={!profileName.trim() || saving}
              startIcon={saving ? <CircularProgress size={20} /> : null}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  )
}

export default InformationEditor
